import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CalendarDays, ChevronLeft, ChevronRight, Search, X,
  Globe2, Shield, TrendingUp, Leaf, Trophy,
  Star, Landmark, Users, Cpu, BookOpen, Newspaper, Loader2,
  AlertCircle, Download, FileText,
} from "lucide-react";
import caApi from "../api/ca";
import SEOHead from "../components/SEOHead";

// ── Category config ────────────────────────────────────────────────────────────
const CATEGORIES = [
  { key: "All",                Icon: Newspaper,   color: "#1A2810", bg: "#F0FDF4", dot: "#16A34A" },
  { key: "National",           Icon: Landmark,    color: "#92400E", bg: "#FEF3C7", dot: "#D97706" },
  { key: "International",      Icon: Globe2,       color: "#1E3A8A", bg: "#EFF6FF", dot: "#3B82F6" },
  { key: "Defence",            Icon: Shield,       color: "#7C2D12", bg: "#FFF7ED", dot: "#EA580C" },
  { key: "Economy",            Icon: TrendingUp,   color: "#064E3B", bg: "#ECFDF5", dot: "#10B981" },
  { key: "Science & Technology", Icon: Cpu,        color: "#312E81", bg: "#EEF2FF", dot: "#6366F1" },
  { key: "Environment",        Icon: Leaf,         color: "#14532D", bg: "#F0FDF4", dot: "#22C55E" },
  { key: "Awards & Honours",   Icon: Trophy,       color: "#78350F", bg: "#FFFBEB", dot: "#F59E0B" },
  { key: "Sports",             Icon: Star,         color: "#831843", bg: "#FDF2F8", dot: "#EC4899" },
  { key: "Government Schemes", Icon: BookOpen,     color: "#1E40AF", bg: "#EFF6FF", dot: "#2563EB" },
  { key: "Appointments",       Icon: Users,        color: "#4C1D95", bg: "#F5F3FF", dot: "#7C3AED" },
];

const IMPORTANCE_COLOR = { high: "#DC2626", medium: "#D97706", low: "#6B7280" };
const IMPORTANCE_BG    = { high: "#FEE2E2", medium: "#FEF3C7", low: "#F3F4F6" };

function catConfig(key) {
  return CATEGORIES.find(c => c.key === key) || CATEGORIES[0];
}

// ── Date helpers ───────────────────────────────────────────────────────────────
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function fmtMonth(dateStr) {
  if (!dateStr) return "";
  const [y, m] = dateStr.split("-");
  return `${MONTHS_FULL[parseInt(m, 10) - 1]} ${y}`;
}
function isCurrentMonth(dateStr) {
  if (!dateStr) return false;
  const today = new Date().toISOString().slice(0, 7); // "YYYY-MM"
  return dateStr.slice(0, 7) === today;
}
// Keep for backward compat
function fmt(dateStr) {
  return fmtMonth(dateStr);
}
function isToday(dateStr) {
  return isCurrentMonth(dateStr);
}

// ── CA card ───────────────────────────────────────────────────────────────────
function CACard({ item, onClick }) {
  const cfg = catConfig(item.category);
  const { Icon } = cfg;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2 }}
      onClick={onClick}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md cursor-pointer overflow-hidden group transition-shadow"
    >
      {/* Top accent stripe */}
      <div className="h-1 w-full" style={{ background: cfg.dot }} />

      <div className="p-4">
        {/* Category + importance row */}
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: cfg.bg }}>
              <Icon size={11} style={{ color: cfg.color }} />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: cfg.color }}>
              {item.category}
            </span>
          </div>
          <span
            className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full"
            style={{ background: IMPORTANCE_BG[item.importance], color: IMPORTANCE_COLOR[item.importance] }}
          >
            {item.importance}
          </span>
        </div>

        {/* Title */}
        <h3 className="font-sora font-bold text-sm text-gray-900 leading-snug mb-2 group-hover:text-green-800 transition-colors line-clamp-2">
          {item.title}
        </h3>

        {/* Summary */}
        {item.summary && (
          <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 mb-3">{item.summary}</p>
        )}

        {/* Footer: facts count + MCQ count */}
        <div className="flex items-center gap-3 pt-2 border-t border-gray-50">
          {item.facts?.length > 0 && (
            <span className="text-[10px] text-gray-400 font-medium">
              {item.facts.length} key facts
            </span>
          )}
          {item.mcqs?.length > 0 && (
            <span className="text-[10px] text-indigo-500 font-semibold">
              {item.mcqs.length} MCQs
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ── Header ────────────────────────────────────────────────────────────────────
function CAHeader() {
  const navigate = useNavigate();
  return (
    <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
        <button onClick={() => navigate("/")}
          className="flex items-center gap-1.5 text-sm font-semibold text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0">
          <ChevronLeft size={15} /> Home
        </button>
        <span className="text-gray-200">|</span>

        {/* Tab switcher */}
        <div className="flex items-center gap-1">
          <button
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-red-600 text-white shadow-sm">
            <Newspaper size={12} /> Current Affairs
          </button>
          <button onClick={() => navigate("/gk")}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-gray-500 hover:bg-green-50 transition-all"
            style={{ color: "#4A5E2A" }}>
            <BookOpen size={12} /> GK Library
          </button>
        </div>

        <span className="ml-auto text-[10px] text-gray-400 font-medium hidden sm:block">Monthly digest · NDA · CDS · AFCAT</span>
      </div>
    </div>
  );
}

// ── Date navigator ─────────────────────────────────────────────────────────────
function DateNav({ selected, dates, onChange }) {
  const idx = dates.indexOf(selected);
  return (
    <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-2xl px-4 py-2.5 shadow-sm">
      <CalendarDays size={15} className="text-gray-400 flex-shrink-0" />
      <button
        disabled={idx >= dates.length - 1}
        onClick={() => onChange(dates[idx + 1])}
        className="p-1 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors"
      >
        <ChevronLeft size={15} className="text-gray-600" />
      </button>
      <span className="font-sora font-bold text-sm text-gray-900 min-w-[130px] text-center">
        {isCurrentMonth(selected) ? "This Month" : fmtMonth(selected)}
      </span>
      <button
        disabled={idx <= 0}
        onClick={() => onChange(dates[idx - 1])}
        className="p-1 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors"
      >
        <ChevronRight size={15} className="text-gray-600" />
      </button>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function PDFDownloadBar({ pdfs }) {
  if (!pdfs.length) return null;
  return (
    <div className="bg-gradient-to-r from-[#1A2810] to-[#2D4A1A] border-b border-green-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3 overflow-x-auto scrollbar-hide">
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <FileText size={13} className="text-green-300" />
          <span className="text-[11px] font-bold text-green-200 uppercase tracking-wide whitespace-nowrap">Monthly PDFs</span>
        </div>
        <div className="w-px h-4 bg-green-700 flex-shrink-0" />
        {pdfs.map(pdf => (
          <a
            key={pdf._id}
            href={caApi.getDownloadUrl(pdf._id)}
            download
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors flex-shrink-0 group"
          >
            <Download size={11} className="text-green-300 group-hover:text-white transition-colors" />
            <span className="text-[11px] font-semibold text-green-100 group-hover:text-white transition-colors whitespace-nowrap">
              {MONTH_NAMES[pdf.month - 1]} {pdf.year}
            </span>
          </a>
        ))}
        <span className="ml-auto text-[10px] text-green-500 flex-shrink-0 whitespace-nowrap">Free download · EduPulse</span>
      </div>
    </div>
  );
}

export default function CurrentAffairsPage() {
  const navigate    = useNavigate();
  const [selDate, setSelDate]       = useState(new Date().toISOString().slice(0, 10));
  const [dates, setDates]           = useState([]);
  const [items, setItems]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQ, setSearchQ]       = useState("");
  const [searchResults, setSearchResults]   = useState(null);
  const [searching, setSearching]   = useState(false);
  const [pdfs, setPdfs]             = useState([]);

  // Load available dates + PDFs on mount
  useEffect(() => {
    caApi.getDates()
      .then(d => { setDates(d); if (d.length) setSelDate(d[0]); })
      .catch(() => setDates([new Date().toISOString().slice(0, 10)]));
    caApi.getPDFs()
      .then(d => setPdfs(Array.isArray(d) ? d : []))
      .catch(() => setPdfs([]));
  }, []);

  // Load items when date or category changes
  const loadItems = useCallback(async () => {
    if (searchResults !== null) return;
    setLoading(true); setError(null);
    try {
      if (activeCategory === "All") {
        const data = await caApi.getDaily(selDate);
        setItems(data.items || []);
      } else {
        const data = await caApi.getByCategory(activeCategory, { date: selDate });
        setItems(data.items || []);
      }
    } catch {
      setError("Failed to load current affairs.");
    } finally {
      setLoading(false);
    }
  }, [selDate, activeCategory, searchResults]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // Search
  useEffect(() => {
    if (!searchQ.trim()) { setSearchResults(null); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await caApi.search(searchQ);
        setSearchResults(data.items || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [searchQ]);

  const displayItems = searchResults !== null ? searchResults : items;
  const activeCfg    = catConfig(activeCategory);

  return (
    <div className="min-h-screen bg-[#F5F6FA] font-body">
      <SEOHead
        title="Daily Current Affairs for NDA, CDS & CAPF 2026 | EduPulseAI"
        description="Free daily current affairs for NDA, CDS, AFCAT and CAPF exam preparation. Categorised by Defence, National, International, Economy, Science & Technology, Sports. Updated every day."
        keywords="current affairs for NDA 2026, current affairs for CDS, UPSC current affairs, defence current affairs today, daily current affairs PDF"
        url="/ca"
        jsonLd={{ "@context": "https://schema.org", "@type": "CollectionPage", "name": "Daily Current Affairs for NDA & CDS", "url": "https://edupulseai.co.in/ca", "publisher": { "@type": "Organization", "name": "EduPulseAI" } }}
      />
      <CAHeader />
      <PDFDownloadBar pdfs={pdfs} />

      {/* ── Hero strip ─────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center gap-3">
          {/* Date nav */}
          {dates.length > 0 && (
            <DateNav selected={selDate} dates={dates} onChange={d => { setSelDate(d); setSearchResults(null); setSearchQ(""); }} />
          )}

          {/* Search */}
          <div className="flex-1 min-w-[200px] max-w-xs relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              placeholder="Search current affairs…"
              className="w-full pl-8 pr-8 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-green-500 transition-colors"
            />
            {searchQ && (
              <button onClick={() => { setSearchQ(""); setSearchResults(null); }} className="absolute right-2.5 top-1/2 -translate-y-1/2">
                <X size={13} className="text-gray-400" />
              </button>
            )}
          </div>

          {/* Stats */}
          {!loading && searchResults === null && (
            <div className="ml-auto text-right">
              <p className="font-sora font-black text-lg text-gray-900 leading-none">{items.length}</p>
              <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">articles</p>
            </div>
          )}
        </div>

        {/* Category scroll row */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-3">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
            {CATEGORIES.map(({ key, Icon: CIcon, color, bg, dot }) => (
              <button
                key={key}
                onClick={() => { setActiveCategory(key); setSearchResults(null); setSearchQ(""); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex-shrink-0 border"
                style={activeCategory === key
                  ? { background: color, color: "#fff", borderColor: color }
                  : { background: "#fff", color: "#374151", borderColor: "#E5E7EB" }
                }
              >
                <CIcon size={11} />
                {key}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Content ────────────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">

        {/* Search mode header */}
        {searchResults !== null && (
          <div className="mb-4 flex items-center gap-2">
            <Search size={14} className="text-gray-400" />
            <span className="text-sm text-gray-600">
              <span className="font-bold text-gray-900">{searchResults.length}</span> results for "{searchQ}"
            </span>
            <button onClick={() => { setSearchQ(""); setSearchResults(null); }}
              className="ml-auto text-xs text-red-500 font-semibold hover:text-red-700">
              Clear search
            </button>
          </div>
        )}

        {/* Normal mode header */}
        {searchResults === null && (
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: activeCfg.bg }}>
              <activeCfg.Icon size={12} style={{ color: activeCfg.color }} />
            </div>
            <h2 className="font-sora font-extrabold text-base text-gray-900">
              {activeCategory === "All" ? (isCurrentMonth(selDate) ? "This Month's Current Affairs" : `CA — ${fmtMonth(selDate)}`) : activeCategory}
            </h2>
          </div>
        )}

        {/* Loading */}
        {(loading || searching) && (
          <div className="flex justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <Loader2 size={28} className="animate-spin text-gray-300" />
              <p className="text-xs text-gray-400">Fetching latest affairs…</p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="flex flex-col items-center py-16 gap-3">
            <AlertCircle size={32} className="text-red-400" />
            <p className="text-sm text-red-500">{error}</p>
          </div>
        )}

        {/* Empty */}
        {!loading && !searching && !error && displayItems.length === 0 && (
          <div className="flex flex-col items-center py-20 gap-3">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
              <Newspaper size={28} className="text-gray-300" />
            </div>
            <p className="text-sm text-gray-400 font-medium">No current affairs found</p>
            <p className="text-xs text-gray-300">
              {isCurrentMonth(selDate) ? "This month's articles will appear soon" : `No articles for ${fmtMonth(selDate)}`}
            </p>
          </div>
        )}

        {/* Grid */}
        {!loading && !searching && displayItems.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {displayItems.map((item, i) => (
              <motion.div key={item._id || item.slug}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: i * 0.02 }}
              >
                <CACard item={item} onClick={() => navigate(`/ca/${item.slug}`)} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
